import {Component, Inject, OnInit} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatDialogTitle, MatDialogClose, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {NotificationService} from '../../notification/notification.service';
import {AuthService} from '../../core/security/auth.service';
import {RolesEnum} from '../../core/security/roles.enum';
import {LocalFaxService} from '../local-fax.service';
import {UpdateFaxNotesRequest} from '../../api/models/update-fax-notes-request';
import { FlexModule } from '@angular/flex-layout';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatTextareaInputComponent } from '../../editors/mat-textarea-input/mat-textarea-input.component';
import { Angular2PromiseButtonModule } from 'angular2-promise-buttons';

export interface FaxNotesEditDialogData {
  faxId: string;
  faxInternalNotes: string;
}

@Component({
    selector: 'app-fax-notes-edit-dialog',
    template: `
    <form [formGroup]="form" novalidate (ngSubmit)="onSubmit()">
      <h1 mat-dialog-title fxLayout="row" fxLayoutAlign="space-between center" style="padding-top: 10px;">
        <div fxFlex>Edit Internal Notes</div>
        <button mat-icon-button [mat-dialog-close]="false" class="button-40">
          <mat-icon>close</mat-icon>
        </button>
      </h1>
      <mat-dialog-content class="mat-typography">
        <app-mat-textarea-input formControlName="faxInternalNotes"
                                label="Internal Notes" name="faxInternalNotes"
                                [submitted]="submitted"></app-mat-textarea-input>
      </mat-dialog-content>
      <mat-dialog-actions align="end">
        <button mat-flat-button type="submit" color="primary" [promiseBtn]="buttonSubmitPromise">Save</button>
      </mat-dialog-actions>
    </form>
  `,
    styles: [],
    standalone: true,
    imports: [FormsModule, ReactiveFormsModule, MatDialogTitle, FlexModule, MatIconButton, MatDialogClose, MatIcon, CdkScrollable, MatDialogContent, MatTextareaInputComponent, MatDialogActions, MatButton, Angular2PromiseButtonModule]
})
export class FaxNotesEditDialogComponent implements OnInit {

  form: UntypedFormGroup;
  faxInternalNotesControl = new UntypedFormControl('', []);

  submitted: boolean;
  serverError: boolean;
  hasEditAccess: boolean;
  hasDeleteAccess: boolean;
  buttonSubmitPromise: Promise<void>;

  constructor(public dialogRef: MatDialogRef<FaxNotesEditDialogComponent>,
              @Inject(MAT_DIALOG_DATA) public data: FaxNotesEditDialogData,
              public dialog: MatDialog,
              public faxService: LocalFaxService,
              private fb: UntypedFormBuilder,
              private notificationService: NotificationService,
              private authService: AuthService
  ) {

  }

  async onSubmit() {
    this.serverError = false;

    if (this.form.valid && this.hasEditAccess) {
      this.buttonSubmitPromise = this.processSubmit();
    }
  }

  async processSubmit(): Promise<void> {
    try {
      this.submitted = true;
      const reqData = this.prepareFormModel();

      if (await this.faxService.saveInternalNotes(reqData)) {
        this.notificationService.notifySuccess('The changes have been submitted successfully');
        this.dialogRef.close(true);
      }
    } catch (e) {
      console.error(e);
      this.notificationService.notifyDanger('Unable to save tag.');
    } finally {
      this.submitted = false;
    }
  }

  prepareFormModel(): UpdateFaxNotesRequest {
    const formModel = this.form.value;

    return {
      faxId: this.data.faxId,
      internalNotes: formModel.faxInternalNotes,
    };
  }

  ngOnInit(): void {
    this.hasEditAccess = this.authService.hasAccess([RolesEnum.Fax_Edit, RolesEnum.Fax_Delete]);
    this.hasDeleteAccess = this.authService.hasAccess([RolesEnum.Fax_Delete]);

    this.createForm();

    if (this.data?.faxInternalNotes) {
      this.form.reset({
        faxInternalNotes: this.data?.faxInternalNotes,
      });
    }
  }

  private createForm() {
    this.form = this.fb.group({
        'faxInternalNotes': this.faxInternalNotesControl,
      },
    );
  }
}
